import React from 'react';

import { Divider, Skeleton } from 'antd';
import { Column, Row, Space, Text } from 'farmhub-ui-core';
import moment from 'moment';

import { useAppSelector } from 'app';
import { NameText } from 'components/Text';
import { selectProductUnit } from 'slice';
import { theme } from 'styled';
import {
  ORDER_STATUS,
  buildOrdinalColumn,
  genWholesaleUnit,
  getDummyArray,
  isWholesaleOrder,
  getOrderPrevStatus,
} from 'utils';

import { CTable } from './styles';

type ExpandTableProps = {
  order: any;
  productItems: any;
  isLoading: boolean;
};

const ExpandTable = ({ order, productItems, isLoading }: ExpandTableProps) => {
  const productUnit = useAppSelector(selectProductUnit);

  const columns: Array<any> = [
    {
      width: '5%',
      title: (
        <Text
          color="black"
          styles={{ whiteSpace: 'nowrap' }}
          weight="extraBold"
        >
          STT
        </Text>
      ),
      key: 'order',
      align: 'center',
      render: (_: any, record: any, index: number) => (
        <NameText color="black">{buildOrdinalColumn(index)}</NameText>
      ),
    },
    {
      width: '24%',
      title: (
        <>
          <Text color="black" weight="extraBold">
            PHIẾU XUẤT KHO
          </Text>
          <br />
          <Text color="black" lineHeight="22px" weight="bold">
            Ngày: {moment(order.createdAt).format('DD/MM/YYYY')}
          </Text>
        </>
      ),
      dataIndex: 'product',
      key: 'product',
      render: (product: any, record: any) => (
        <Column width="100%">
          <Text
            color="black"
            size="sm"
            styles={{ width: '100%', display: 'block' }}
            weight="bold"
          >
            {product?.name}
          </Text>
          <Text color="black" size="xxs">
            {!isWholesaleOrder(order?.type) ? (
              <Row alignItems="center">
                <Text color="black" size="xxs">
                  {product?.code || '--'}
                </Text>
                <Space width={8} />
                <Divider
                  style={{
                    top: '0px',
                    margin: 0,
                    border: `1px solid ${theme.colors.border.main}`,
                  }}
                  type="vertical"
                />
                <Space width={8} />
                <Text color="black" size="xxs">
                  {genWholesaleUnit({
                    unitPrefix:
                      productUnit.prefixObj[
                        record.supplierProductRetailUnit?.prefix
                      ],
                    unitWeight: record.supplierProductRetailUnit?.weight,
                    unitPostfix:
                      productUnit.postfixObj[
                        record.supplierProductRetailUnit?.postfix
                      ],
                  })}
                </Text>
              </Row>
            ) : (
              <Row>
                <Text color="black" size="xxs">
                  {product?.code || '--'}
                </Text>
                <Space width={8} />
                <Divider
                  style={{
                    top: '0px',
                    margin: 0,
                    border: `1px solid ${theme.colors.border.main}`,
                  }}
                  type="vertical"
                />
                <Space width={8} />
                <Text color="black" size="xxs">
                  {genWholesaleUnit({
                    unitPrefix:
                      productUnit.prefixObj[
                        record.customerProductWholesaleUnit?.prefix
                          ? record.customerProductWholesaleUnit?.prefix
                          : record.supplierProductWholesaleUnit?.prefix
                      ],
                    unitWeight:
                      record.customerProductWholesaleUnit?.weight != null
                        ? record.customerProductWholesaleUnit?.weight
                        : record.supplierProductWholesaleUnit?.weight,
                    unitPostfix:
                      productUnit.postfixObj[
                        record.customerProductWholesaleUnit?.postfix
                          ? record.customerProductWholesaleUnit?.postfix
                          : record.supplierProductWholesaleUnit?.postfix
                      ],
                  })}
                </Text>
              </Row>
            )}
          </Text>
        </Column>
      ),
    },
    {
      align: 'center',
      width: '12%',
      title: (
        <>
          <Text color="black" size="tiny" weight="extraBold">
            S.LƯỢNG
          </Text>{' '}
          <br />
          <Text color="black" weight="extraBold">
            ĐẶT
          </Text>
        </>
      ),
      dataIndex: 'customerQuantity',
      key: 'customerQuantity',
      render: (customerQuantity: number) => (
        <Text
          color={customerQuantity === 0 ? 'red' : 'black'}
          size="sm"
          weight="bold"
        >
          {customerQuantity}
        </Text>
      ),
    },
    {
      align: 'center',
      width: '12%',
      title: (
        <>
          <Text color="black" size="tiny" weight="extraBold">
            S.LƯỢNG
          </Text>{' '}
          <br />
          <Text color="black" weight="extraBold">
            CHỐT
          </Text>
        </>
      ),
      dataIndex: 'supplierQuantity',
      key: 'supplierQuantity',
      render: (supplierQuantity: number) => (
        <Text
          color={supplierQuantity === 0 ? 'red' : 'black'}
          size="sm"
          weight="bold"
        >
          {[
            ORDER_STATUS.PROCESSING,
            ORDER_STATUS.DELIVERY,
            ORDER_STATUS.CUSTOMER_CONFIRM,
            ORDER_STATUS.SUPPLIER_CONFIRM,
            ORDER_STATUS.COMPLETE,
          ].includes(order.status) ||
          ([ORDER_STATUS.REJECT, ORDER_STATUS.MERGED].includes(order.status) &&
            [
              ORDER_STATUS.PROCESSING,
              ORDER_STATUS.DELIVERY,
              ORDER_STATUS.CUSTOMER_CONFIRM,
              ORDER_STATUS.SUPPLIER_CONFIRM,
              ORDER_STATUS.COMPLETE,
            ].includes(getOrderPrevStatus(order.status, order.trackingStatus)))
            ? supplierQuantity
            : ''}
        </Text>
      ),
    },
    {
      align: 'center',
      width: '12%',
      title: (
        <>
          <Text color="black" size="tiny" weight="extraBold">
            S.LƯỢNG
          </Text>{' '}
          <br />
          <Text color="black" weight="extraBold">
            GIAO
          </Text>
        </>
      ),
      dataIndex: 'supplierDeliveredQuantity',
      key: 'supplierDeliveredQuantity',
      render: (supplierDeliveredQuantity: number) => (
        <Text
          color={supplierDeliveredQuantity === 0 ? 'red' : 'black'}
          size="sm"
          weight="bold"
        >
          {[
            ORDER_STATUS.DELIVERY,
            ORDER_STATUS.CUSTOMER_CONFIRM,
            ORDER_STATUS.SUPPLIER_CONFIRM,
            ORDER_STATUS.COMPLETE,
          ].includes(order.status) ||
          ([ORDER_STATUS.REJECT, ORDER_STATUS.MERGED].includes(order.status) &&
            [
              ORDER_STATUS.DELIVERY,
              ORDER_STATUS.CUSTOMER_CONFIRM,
              ORDER_STATUS.SUPPLIER_CONFIRM,
              ORDER_STATUS.COMPLETE,
            ].includes(getOrderPrevStatus(order.status, order.trackingStatus)))
            ? supplierDeliveredQuantity
            : ''}
        </Text>
      ),
    },
    {
      align: 'center',
      width: '12%',
      title: (
        <>
          <Text color="black" size="tiny" weight="extraBold">
            S.LƯỢNG
          </Text>{' '}
          <br />
          <Text color="black" weight="extraBold">
            NHẬN
          </Text>
        </>
      ),
      dataIndex: 'customerReceivedQuantity',
      key: 'customerReceivedQuantity',
      render: (customerReceivedQuantity: number) => (
        <Text
          color={customerReceivedQuantity === 0 ? 'red' : 'black'}
          size="sm"
          weight="bold"
        >
          {[
            ORDER_STATUS.CUSTOMER_CONFIRM,
            ORDER_STATUS.SUPPLIER_CONFIRM,
            ORDER_STATUS.COMPLETE,
          ].includes(order.status) ||
          ([ORDER_STATUS.REJECT, ORDER_STATUS.MERGED].includes(order.status) &&
            [
              ORDER_STATUS.CUSTOMER_CONFIRM,
              ORDER_STATUS.SUPPLIER_CONFIRM,
              ORDER_STATUS.COMPLETE,
            ].includes(getOrderPrevStatus(order.status, order.trackingStatus)))
            ? customerReceivedQuantity
            : ''}
        </Text>
      ),
    },
    {
      width: '22%',
      title: (
        <>
          <Text color="black" weight="extraBold">
            GHI CHÚ KH
          </Text>{' '}
          <br />
          <Text color="black" weight="extraBold">
            GHI CHÚ NCC
          </Text>
        </>
      ),
      key: 'price',
      render: record => (
        <Column>
          <Row alignItems="flex-start" height="50%">
            <Text color="black" weight="bold">
              {record.customerNote || ''}
            </Text>
          </Row>
          <Row alignItems="flex-start" height="50%">
            <Text color="black" weight="bold">
              {record.supplierNote || ''}
            </Text>
          </Row>
        </Column>
      ),
    },
  ];

  return (
    <CTable
      bordered
      columns={isLoading ? columnsSkeleton : columns}
      dataSource={isLoading ? getDummyArray(10) : productItems ?? []}
      isLoading={isLoading}
      locale={{
        emptyText: (
          <Text color="black">Hiện tại không có sản phẩm thống kê</Text>
        ),
      }}
      pagination={false}
      showHeader={false}
    />
  );
};

export default ExpandTable;

const columnsSkeleton: Array<any> = [
  {
    width: '6%',
    title: (
      <Skeleton.Input
        size="small"
        style={{ width: '25px', alignItems: 'center' }}
      />
    ),
    key: 'order',
    render: () => (
      <Skeleton.Input
        size="small"
        style={{ width: '25px', alignItems: 'center' }}
      />
    ),
  },
  {
    width: '24%',
    title: (
      <Skeleton.Input
        size="small"
        style={{ width: '160px', alignItems: 'center' }}
      />
    ),
    key: 'product',
    render: () => (
      <Skeleton.Input
        size="small"
        style={{ width: '160px', alignItems: 'center' }}
      />
    ),
  },
  {
    align: 'center',
    width: '12%',
    title: (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
    key: 'customerQuantity',
    render: () => (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
  },
  {
    align: 'center',
    width: '12%',
    title: (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
    key: 'supplierQuantity',
    render: () => (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
  },
  {
    align: 'center',
    width: '12%',
    title: (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
    key: 'supplierDeliveredQuantity',
    render: () => (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
  },
  {
    align: 'center',
    width: '12%',
    title: (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
    key: 'customerReceivedQuantity',
    render: () => (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
  },
  {
    width: '20%',
    title: (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
    key: 'price',
    render: () => (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
  },
];
